import { API_BASE_URL } from "../config";
import { getAccessToken as getTokenFromAuth } from "./auth-helper";
import * as $ from "jquery";
import type { AudienceGroup } from "./audience-api";

export interface Campaign {
  id: number;
  name: string;
  content?: string;
  startsAtUtc?: string | null;
  endsAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc?: string | null;
  signatureIds?: number[];
  targetAll: boolean;
  targetGroups: AudienceGroup[];
}

export interface SignatureCampaignSummary {
  signatureId: number;
  activeCampaignCount: number;
}

export interface SignatureCampaignMappingItem {
  campaignId: number;
  name: string;
  startsAtUtc?: string | null;
  endsAtUtc?: string | null;
  status: string;
  assigned: boolean;
}

export interface SignatureCampaignMappingsResponse {
  signatureId: number;
  signatureName: string;
  campaigns: SignatureCampaignMappingItem[];
}

function getAccessToken(): string {
  const token = getTokenFromAuth();
  if (!token) {
    throw new Error("not_authenticated");
  }
  return token;
}

export async function getAllCampaigns(): Promise<Campaign[]> {
  const token = getAccessToken();
  return $.ajax({
    type: "GET",
    url: `${API_BASE_URL}/api/campaigns`,
    contentType: "application/json",
    headers: { Authorization: `Bearer ${token}` },
    cache: false,
    crossDomain: true,
    xhrFields: { withCredentials: false },
  });
}

export async function getCampaign(id: number): Promise<Campaign> {
  const token = getAccessToken();
  return $.ajax({
    type: "GET",
    url: `${API_BASE_URL}/api/campaigns/${id}`,
    contentType: "application/json",
    headers: { Authorization: `Bearer ${token}` },
    cache: false,
    crossDomain: true,
    xhrFields: { withCredentials: false },
  });
}

export async function createCampaign(payload: {
  name: string;
  content: string;
  startsAtUtc?: string | null;
  endsAtUtc?: string | null;
  signatureIds: number[];
  targetAll: boolean;
  targetGroupIds: string[];
}): Promise<Campaign> {
  const token = getAccessToken();
  return $.ajax({
    type: "POST",
    url: `${API_BASE_URL}/api/campaigns`,
    contentType: "application/json",
    headers: { Authorization: `Bearer ${token}` },
    data: JSON.stringify(payload),
    cache: false,
    crossDomain: true,
    xhrFields: { withCredentials: false },
  });
}

export async function updateCampaign(
  id: number,
  payload: {
    name: string;
    content: string;
    startsAtUtc?: string | null;
    endsAtUtc?: string | null;
    signatureIds: number[];
    targetAll: boolean;
    targetGroupIds: string[];
  }
): Promise<void> {
  const token = getAccessToken();
  return $.ajax({
    type: "PUT",
    url: `${API_BASE_URL}/api/campaigns/${id}`,
    contentType: "application/json",
    headers: { Authorization: `Bearer ${token}` },
    data: JSON.stringify(payload),
    cache: false,
    crossDomain: true,
    xhrFields: { withCredentials: false },
  });
}

export async function deleteCampaign(id: number): Promise<void> {
  const token = getAccessToken();
  return $.ajax({
    type: "DELETE",
    url: `${API_BASE_URL}/api/campaigns/${id}`,
    contentType: "application/json",
    headers: { Authorization: `Bearer ${token}` },
    cache: false,
    crossDomain: true,
    xhrFields: { withCredentials: false },
  });
}

export async function getSignatureCampaignSummaries(): Promise<SignatureCampaignSummary[]> {
  const token = getAccessToken();
  return $.ajax({
    type: "GET",
    url: `${API_BASE_URL}/api/signatures/campaign-summaries`,
    contentType: "application/json",
    headers: { Authorization: `Bearer ${token}` },
    cache: false,
    crossDomain: true,
    xhrFields: { withCredentials: false },
  });
}

export async function getSignatureCampaignMappings(
  signatureId: number
): Promise<SignatureCampaignMappingsResponse> {
  const token = getAccessToken();
  return $.ajax({
    type: "GET",
    url: `${API_BASE_URL}/api/signatures/${signatureId}/campaign-mappings`,
    contentType: "application/json",
    headers: { Authorization: `Bearer ${token}` },
    cache: false,
    crossDomain: true,
    xhrFields: { withCredentials: false },
  });
}

export async function updateSignatureCampaignMappings(
  signatureId: number,
  campaignIds: number[]
): Promise<void> {
  const token = getAccessToken();
  return $.ajax({
    type: "PUT",
    url: `${API_BASE_URL}/api/signatures/${signatureId}/campaign-mappings`,
    contentType: "application/json",
    headers: { Authorization: `Bearer ${token}` },
    data: JSON.stringify({ campaignIds }),
    cache: false,
    crossDomain: true,
    xhrFields: { withCredentials: false },
  });
}
